import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { AppAsyncThunkOpts } from 'models/Store';
import { deleteSession } from 'store/customer';
import { createNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import axiosInstance from 'utils/axios.utils';

import { clearShipmentList } from '../list/shipmentList.slice';

export const saveNickname = createAsyncThunk<
  string | undefined,
  { trackingId: string; nickname?: string },
  AppAsyncThunkOpts
>('nickname/saveNickname', async ({ trackingId, nickname }, thunkAPI) => {
  const { dispatch } = thunkAPI;
  try {
    const response = await apiUpdateNickname(trackingId, nickname);
    const { status } = response;
    if (status !== 200) return;
    return nickname;
  } catch (error) {
    const authError = error as AuthError;
    if (
      (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
      (authError.isLoginRequiredError && authError.isLoginRequiredError())
    ) {
      const status = 401;
      if (status) handleNicknameError(dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        data: authError.message,
        isAxiosError: false,
      });
    }

    if (axios.isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      const status = axiosErr.response?.status;
      if (status) handleNicknameError(dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        data: axiosErr.response?.data,
        isAxiosError: axiosErr.isAxiosError,
      });
    }

    throw error;
  }
});

/**
 * Makes an API call to patch shipment from Shipments Gateway API to update or remove the nickname field
 * @param trackingId The tracking ID of the shipment to get
 * @param nickname Replace or remove the nickname that is attached to tracking ID
 */
const apiUpdateNickname = (trackingId: string, nickname?: string) => {
  const queryString = nickname ? '' : '?trackingId=' + trackingId;
  const _nicknamesApi = `${apiEndpoint.nicknames}/nicknames${queryString}`;
  const requestConfig = APP_REQUEST_CONFIGS.generic;
  const body = {
    trackingId,
    nickname,
  };
  // add/update nickname with given nickname input
  if (nickname) {
    return axiosInstance.post(_nicknamesApi, body, requestConfig);
  }
  // delete nickname if nickname is empty
  return axiosInstance.delete(_nicknamesApi);
};

const handleNicknameError = (dispatch: AppDispatch, status: number) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.ERROR,
    message: errorContent.nicknameError,
  };
  if (status === 401) {
    notification.status = NotificationStatus.INFO;
    notification.message = errorContent.expiredSession;
    dispatch(clearShipmentList());
    dispatch(deleteSession());
    return dispatch(createNotification(notification));
  }
  return dispatch(createNotification(notification));
};
